import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { observable, syncState } from "@legendapp/state";
import { useValue } from "@legendapp/state/react";
import { AvatarDisplay } from "@repo/ui/components/avatar-display";
import { Icon } from "@repo/ui/components/icon";
import { ScrollFade } from "@repo/ui/components/scroll-fade";
import { useSidebarMobileClick } from "@repo/ui/hooks/use-sidebar-mobile-click";
import { Input } from "@repo/ui/input";
import { Button } from "@repo/ui/ui/button";
import {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@repo/ui/ui/sidebar";
import { Skeleton } from "@repo/ui/ui/skeleton";
import { createLink } from "@tanstack/react-router";
import { useRef, useState } from "react";

import { prefetchBoard, usePrefetchHandlers } from "~/stores/board-payloads";
import { boards$ } from "~/stores/boards";
import { createBoard, reorderBoard } from "~/stores/boards/boards.actions";
import { BoardOptions } from "./board-options";

const SidebarMenuButtonLink = createLink(SidebarMenuButton);

const sortedBoardIds$ = observable<string[]>(() => {
  const boards = boards$.get() ?? {};
  return Object.values(boards)
    .filter((board) => !board.favorite)
    .sort((a, b) => {
      const orderA = a.order ?? "a0";
      const orderB = b.order ?? "a0";
      return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
    })
    .map((board) => board.id);
});

export const BoardsList = () => {
  const isEmpty = useValue(() => Object.keys(boards$).length === 0);
  const isLoading = useValue(() => !syncState(boards$).isLoaded.get());
  const boardIds = useValue(sortedBoardIds$);
  const [localIds, setLocalIds] = useState<string[] | null>(null);
  const displayIds = localIds ?? boardIds;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = displayIds.indexOf(active.id as string);
      const newIndex = displayIds.indexOf(over.id as string);
      const newOrder = arrayMove(displayIds, oldIndex, newIndex);
      setLocalIds(newOrder);
      reorderBoard(active.id as string, newIndex, displayIds);
    }
    setTimeout(() => setLocalIds(null), 100);
  };

  if (isLoading) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (isEmpty) {
    return (
      <div className="space-y-3 px-2 pb-2">
        <p className="text-sidebar-foreground/70 text-xs">
          Display your data in powerful ways.
        </p>
        <Button variant="secondary" className="w-full" onClick={createBoard}>
          <Icon name="plus" className="text-muted-foreground" />
          Create a dashboard
        </Button>
      </div>
    );
  }
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={displayIds}
        strategy={verticalListSortingStrategy}
      >
        <ScrollFade
          className="no-scrollbar -m-1 max-h-50 p-1"
          fromClassName="from-sidebar"
        >
          <SidebarMenu>
            {displayIds.map((id) => (
              <BoardItem key={id} id={id} />
            ))}
          </SidebarMenu>
        </ScrollFade>
      </SortableContext>
    </DndContext>
  );
};

export const BoardItem = ({ id }: { id: string }) => {
  const handleLinkClick = useSidebarMobileClick("left");

  const name = useValue(boards$[id].name);
  const avatar = useValue(boards$[id].avatar);

  const [isRenaming, setIsRenaming] = useState(false);
  const renameOriginalNameRef = useRef<string>("");

  const prefetchHandlers = usePrefetchHandlers(prefetchBoard, id);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = (e: React.MouseEvent) => {
    // While renaming, the inline input swaps in for the label and stops its
    // own click propagation; this guard is a safety net so a stray click on
    // the row doesn't navigate away mid-rename.
    if (isRenaming) {
      e.preventDefault();
      return;
    }
    handleLinkClick();
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    renameOriginalNameRef.current = name ?? "";
    setIsRenaming(true);
  };

  const handleRenameBlur = () => {
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // The row spreads dnd-kit's drag listeners; its KeyboardSensor would
    // otherwise swallow Space (its drag-activation key) before it reaches the
    // input. Stop propagation so typing—spaces included—works during rename.
    e.stopPropagation();
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
    if (e.key === "Escape") {
      const currentValue = e.currentTarget.value.trim();
      const original = renameOriginalNameRef.current;
      if (currentValue === "") {
        boards$[id].name.set(original);
      }
      e.currentTarget.blur();
    }
  };

  return (
    <BoardOptions type="context" boardId={id}>
      <SidebarMenuItem ref={setNodeRef} style={style}>
        <SidebarMenuButtonLink
          name="left"
          to="/boards/$boardId"
          params={{ boardId: id }}
          preload="intent"
          activeProps={{ isActive: true }}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          {...prefetchHandlers}
          {...attributes}
          {...listeners}
        >
          <AvatarDisplay avatar={avatar} fallbackIcon="dashboard" />
          {isRenaming ? (
            <Input
              value={name ?? ""}
              onChange={(e) => boards$[id].name.set(e.target.value)}
              onBlur={handleRenameBlur}
              onKeyDown={handleRenameKeyDown}
              placeholder="Untitled board"
              className="h-auto min-w-0 flex-1 rounded-none border-0 bg-transparent p-0 text-sm shadow-none focus-visible:border-0 focus-visible:shadow-none focus-visible:ring-0 dark:bg-transparent"
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="truncate">{name || "Untitled board"}</span>
          )}
        </SidebarMenuButtonLink>
        <BoardOptions boardId={id}>
          <SidebarMenuAction
            showOnHover
            className="rounded-sm data-[state=open]:bg-accent"
          >
            <Icon name="more-horizontal" />
            <span className="sr-only">More</span>
          </SidebarMenuAction>
        </BoardOptions>
      </SidebarMenuItem>
    </BoardOptions>
  );
};

/**
 * Read-only "you are here" stub for the active board, rendered under the
 * Dashboards section header when the section is collapsed. Mirrors BoardItem's
 * visuals minus drag, rename, and the actions menu.
 */
export const ActiveBoardItem = ({ id }: { id: string }) => {
  const name = useValue(boards$[id].name);
  const avatar = useValue(boards$[id].avatar);

  return (
    <SidebarMenuItem>
      <SidebarMenuButtonLink
        name="left"
        to="/boards/$boardId"
        params={{ boardId: id }}
        preload="intent"
        activeProps={{ isActive: true }}
      >
        <AvatarDisplay avatar={avatar} fallbackIcon="dashboard" />
        <span className="truncate">{name || "Untitled board"}</span>
      </SidebarMenuButtonLink>
    </SidebarMenuItem>
  );
};
